import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import productAPI from '../../api/product';
import componentsAPI from '../../api/components';
import './pages.css';

const SectionEditor = ({ section, onUpdate, onDelete, onClose }) => {
  const [sectionName, setSectionName] = useState(section.sectionName || '');
  const [content, setContent] = useState(section.content || {});
  const [styles, setStyles] = useState(section.styles || {});
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [componentSchema, setComponentSchema] = useState(null);
  const [styleSchema, setStyleSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'styles'
  const [styleSearchQuery, setStyleSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({
    'section': true,
    'colors': true
  });
  const [previewContent, setPreviewContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setSectionName(section.sectionName || '');
    setContent(section.content || {});
    setStyles(section.styles || {});
    setHasChanges(false);
    fetchComponentSchema();
  }, [section]);

  const fetchComponentSchema = async () => {
    try {
      setLoading(true);
      const response = await componentsAPI.getAllComponents();

      console.log('API Response:', response);

      if (response?.data?.status === 200 || response?.data?.success) {
        const components = response.data.data || [];
        console.log('All components:', components);

        const component = components.find((c) => c.componentKey === section.componentKey);
        console.log('Component found:', component);
        console.log('Component styleSchema raw:', component?.styleSchema);
        console.log('Component styleSchema type:', typeof component?.styleSchema);
        console.log('Component defaultStyles raw:', component?.defaultStyles);
        console.log('Component defaultStyles type:', typeof component?.defaultStyles);

        if (component) {
          let schema = component.contentSchema;

          // Handle if schema is an object/map
          if (schema && typeof schema === 'object' && Object.keys(schema).length > 0) {
            console.log('Setting schema with keys:', Object.keys(schema));
            setComponentSchema(schema);
          } else {
            console.warn('Schema is empty or invalid for component:', section.componentKey);
          }

          // Set style schema - use styleSchema if available AND not empty, otherwise fallback to defaultStyles
          console.log('component.styleSchema exists?', !!component.styleSchema);
          console.log('component.styleSchema keys:', component.styleSchema ? Object.keys(component.styleSchema).length : 0);
          console.log('component.defaultStyles keys:', component.defaultStyles ? Object.keys(component.defaultStyles).length : 0);

          // Check if styleSchema has actual keys (not just empty object)
          const hasStyleSchema = component.styleSchema && Object.keys(component.styleSchema).length > 0;
          const styleSchemaData = hasStyleSchema ? component.styleSchema : component.defaultStyles;

          if (styleSchemaData && typeof styleSchemaData === 'object' && Object.keys(styleSchemaData).length > 0) {
            console.log('Setting style schema with', Object.keys(styleSchemaData).length, 'keys');
            console.log('Using:', hasStyleSchema ? 'styleSchema' : 'defaultStyles');
            console.log('First 5 keys:', Object.keys(styleSchemaData).slice(0, 5));
            setStyleSchema(styleSchemaData);
          } else {
            console.warn('No style data available for component:', section.componentKey);
          }
        } else {
          console.warn('Component not found:', section.componentKey);
        }
      }
    } catch (error) {
      console.error('Error fetching component schema:', error);
      toast.error('Failed to load component schema');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (key, value) => {
    setContent((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleStyleChange = (key, value) => {
    setStyles((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(content, styles, sectionName);
    setHasChanges(false);
  };

  const handlePreview = () => {
    // For custom_section, prepare preview content
    if (section.componentKey === 'custom_section') {
      setPreviewContent({
        html: content.html || '',
        css: content.css || '',
        javascript: content.javascript || ''
      });
      setShowPreview(true);
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Smart grouping logic for style fields
  const groupStyleFields = (styleSchema) => {
    const groups = {
      section: { label: 'Section & Layout', icon: 'fa-layer-group', fields: [] },
      gradient: { label: 'Gradient', icon: 'fa-fill-drip', fields: [] },
      heading: { label: 'Heading / Title', icon: 'fa-heading', fields: [] },
      text: { label: 'Text & Description', icon: 'fa-font', fields: [] },
      button: { label: 'Button / CTA', icon: 'fa-square', fields: [] },
      card: { label: 'Cards', icon: 'fa-window-maximize', fields: [] },
      hover: { label: 'Hover & Active Effects', icon: 'fa-hand-pointer', fields: [] },
      icon: { label: 'Icons', icon: 'fa-icons', fields: [] },
      table: { label: 'Table', icon: 'fa-table', fields: [] },
      accordion: { label: 'Accordion / FAQ', icon: 'fa-bars', fields: [] },
      side: { label: 'Side Column', icon: 'fa-columns', fields: [] },
      other: { label: 'Other Styles', icon: 'fa-cog', fields: [] }
    };

    Object.entries(styleSchema).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();

      // Gradient fields
      if (lowerKey.includes('gradient')) {
        groups.gradient.fields.push([key, value]);
      }
      // Heading/Title fields
      else if (lowerKey.includes('heading') || lowerKey.includes('title') || lowerKey.includes('maintitle') || lowerKey.includes('mainheading')) {
        groups.heading.fields.push([key, value]);
      }
      // Text/Description fields
      else if (lowerKey.includes('text') || lowerKey.includes('description') || lowerKey.includes('subtitle') || lowerKey.includes('bio')) {
        groups.text.fields.push([key, value]);
      }
      // Button fields
      else if (lowerKey.includes('button') || lowerKey.includes('cta')) {
        groups.button.fields.push([key, value]);
      }
      // Card fields
      else if (lowerKey.includes('card')) {
        groups.card.fields.push([key, value]);
      }
      // Hover effects
      else if (lowerKey.includes('hover') || lowerKey.includes('active')) {
        groups.hover.fields.push([key, value]);
      }
      // Icon fields
      else if (lowerKey.includes('icon') || lowerKey.includes('check') || lowerKey.includes('close')) {
        groups.icon.fields.push([key, value]);
      }
      // Table fields
      else if (lowerKey.includes('table') || lowerKey.includes('cell') || lowerKey.includes('column') || lowerKey.includes('row') || lowerKey.includes('feature') || lowerKey.includes('brand') || lowerKey.includes('others')) {
        groups.table.fields.push([key, value]);
      }
      // Accordion/FAQ fields
      else if (lowerKey.includes('accordion') || lowerKey.includes('question') || lowerKey.includes('answer') || lowerKey.includes('faq')) {
        groups.accordion.fields.push([key, value]);
      }
      // Side column fields
      else if (lowerKey.includes('side') || lowerKey.includes('sidecontent') || lowerKey.includes('sidecolumn')) {
        groups.side.fields.push([key, value]);
      }
      // Section/Layout fields
      else if (lowerKey.includes('background') || lowerKey.includes('padding') || lowerKey.includes('minheight') || lowerKey.includes('maxwidth') || lowerKey.includes('gap') || lowerKey.includes('alignment') || lowerKey.includes('container')) {
        groups.section.fields.push([key, value]);
      }
      // Everything else
      else {
        groups.other.fields.push([key, value]);
      }
    });

    // Filter out empty groups
    return Object.entries(groups)
      .filter(([, group]) => group.fields.length > 0)
      .reduce((acc, [key, group]) => {
        acc[key] = group;
        return acc;
      }, {});
  };

  // Render individual style field
  const renderStyleField = (styleKey, schemaOrValue) => {
    const isSchemaObject = schemaOrValue && typeof schemaOrValue === 'object' && 'default' in schemaOrValue;
    const defaultValue = isSchemaObject ? schemaOrValue.default : schemaOrValue;
    const fieldLabel = isSchemaObject ? schemaOrValue.label : styleKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    const fieldType = isSchemaObject ? schemaOrValue.type : (typeof defaultValue);

    const currentValue = styles[styleKey] !== undefined ? styles[styleKey] : defaultValue;

    // Detect field type
    const lowerKey = styleKey.toLowerCase();
    const isColor = fieldType === 'color' ||
      lowerKey.includes('color') ||
      lowerKey.includes('background') ||
      lowerKey.includes('gradientstart') ||
      lowerKey.includes('gradientend');
    const isBoolean = fieldType === 'boolean' || typeof defaultValue === 'boolean';
    const isSelect = styleKey === 'gradientType' || styleKey === 'imagePosition' || styleKey === 'layoutType' || styleKey === 'contentType';

    // Boolean fields (toggle switch)
    if (isBoolean) {
      return (
        <div key={styleKey} className="row mb-2">
          <label className="col-sm-5 col-form-label small">{fieldLabel}</label>
          <div className="col-sm-7">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={currentValue || false}
                onChange={(e) => handleStyleChange(styleKey, e.target.checked)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Select fields
    if (isSelect) {
      let options = [];
      if (styleKey === 'gradientType') {
        options = [
          { value: 'linear', label: 'Linear' },
          { value: 'radial', label: 'Radial' }
        ];
      } else if (styleKey === 'imagePosition') {
        options = [
          { value: 'left', label: 'Left' },
          { value: 'right', label: 'Right' }
        ];
      } else if (styleKey === 'layoutType') {
        options = [
          { value: 'default', label: 'Default' },
          { value: 'with-left-content', label: 'With Left Content' },
          { value: 'with-right-content', label: 'With Right Content' }
        ];
      } else if (styleKey === 'contentType') {
        options = [
          { value: 'title-description', label: 'Title & Description' },
          { value: 'image-title', label: 'Image & Title' }
        ];
      }

      return (
        <div key={styleKey} className="row mb-2">
          <label className="col-sm-5 col-form-label small">{fieldLabel}</label>
          <div className="col-sm-7">
            <select
              className="form-select form-select-sm"
              value={currentValue || defaultValue}
              onChange={(e) => handleStyleChange(styleKey, e.target.value)}
            >
              {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    // Color fields (color picker + text input)
    if (isColor) {
      // Convert color value to hex for the color picker
      const getHexValue = (val) => {
        if (!val || val === 'transparent' || val === 'none' || val.startsWith('rgba')) {
          return '#ffffff';
        }
        return val;
      };

      return (
        <div key={styleKey} className="row mb-2">
          <label className="col-sm-5 col-form-label small">{fieldLabel}</label>
          <div className="col-sm-7">
            <div className="input-group input-group-sm">
              <input
                type="color"
                className="form-control form-control-color"
                value={getHexValue(currentValue)}
                onChange={(e) => handleStyleChange(styleKey, e.target.value)}
                style={{ minWidth: '40px' }}
              />
              <input
                type="text"
                className="form-control"
                value={currentValue || ''}
                onChange={(e) => handleStyleChange(styleKey, e.target.value)}
                placeholder={defaultValue}
              />
            </div>
          </div>
        </div>
      );
    }

    // Text fields (everything else)
    return (
      <div key={styleKey} className="row mb-2">
        <label className="col-sm-5 col-form-label small">{fieldLabel}</label>
        <div className="col-sm-7">
          <input
            type="text"
            className="form-control form-control-sm"
            value={currentValue || ''}
            onChange={(e) => handleStyleChange(styleKey, e.target.value)}
            placeholder={defaultValue}
          />
        </div>
      </div>
    );
  };

  // Image upload handler
  const handleImageUpload = async (file, onSuccess) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('photos', file);

      const response = await productAPI.uploadImage(formData);

      const imageUrl = response?.data?.data?.photos?.[0]?.location;
      if (imageUrl) {
        onSuccess(imageUrl);
        toast.success('Image uploaded successfully');
      } else {
        console.error('Unexpected response structure:', response);
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  // === ARRAY OF OBJECTS (schema-driven) ===
  const renderArrayOfObjects = (fieldKey, fieldSchema) => {
    const items = content[fieldKey] || [];
    const fields = fieldSchema.fields || {};

    // Helper to render a single field within an item
    const renderItemField = (item, index, fieldName, fieldDef) => {
      const uploadKey = `${fieldKey}-${index}-${fieldName}`;
      const isUploading = uploadingImages[uploadKey];

      // Handle image/icon/file fields
      if (fieldDef.type === 'image' || fieldDef.type === 'file' || fieldDef.type === 'object') {
        const imageUrl = item[fieldName]?.location || '';

        return (
          <div key={fieldName} className="mb-3">
            <label className="form-label fw-semibold small">
              <i className="fa fa-image me-1 text-primary"></i>
              {fieldDef.label || fieldName}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </label>

            {/* Image Preview */}
            {imageUrl ? (
              <div className="mb-2 position-relative">
                <img
                  src={imageUrl}
                  alt={item.title || `${fieldName} ${index + 1}`}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'contain',
                    border: '2px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                    padding: '0.5rem',
                  }}
                />
                <div className="position-absolute top-0 end-0 m-1">
                  <span className="badge bg-success">
                    <i className="fa fa-check"></i>
                  </span>
                </div>
              </div>
            ) : (
              <div
                className="mb-2 d-flex flex-column align-items-center justify-content-center bg-light rounded"
                style={{
                  width: '100%',
                  height: '150px',
                  border: '2px dashed #dee2e6',
                }}
              >
                <i className="fa fa-cloud-upload-alt fa-3x text-muted mb-2"></i>
                <small className="text-muted">No image</small>
              </div>
            )}

            {/* Upload Button */}
            <label className={`btn btn-sm ${imageUrl ? 'btn-outline-primary' : 'btn-primary'} w-100`} style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
              <i className={`fa ${isUploading ? 'fa-spinner fa-spin' : 'fa-upload'} me-1`}></i>
              {isUploading ? 'Uploading...' : imageUrl ? 'Replace' : 'Upload'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));
                    handleImageUpload(file, (url) => {
                      const newItems = [...items];
                      newItems[index] = {
                        ...newItems[index],
                        [fieldName]: { location: url },
                      };
                      handleContentChange(fieldKey, newItems);
                      setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
                    });
                  }
                  e.target.value = null;
                }}
                disabled={isUploading}
              />
            </label>
          </div>
        );
      }

      // Handle text fields
      if (fieldDef.type === 'string') {
        const currentValue = item[fieldName] || '';
        return (
          <div key={fieldName} className="mb-3">
            <label className="form-label fw-semibold small">
              <i className="fa fa-font me-1 text-primary"></i>
              {fieldDef.label || fieldName}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </label>
            <input
              type="text"
              className="form-control"
              value={currentValue}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = { ...newItems[index], [fieldName]: e.target.value };
                handleContentChange(fieldKey, newItems);
              }}
              placeholder={fieldDef.placeholder || `Enter ${fieldDef.label || fieldName}`}
              maxLength={fieldDef.maxLength}
            />
            {currentValue && fieldDef.maxLength && (
              <small className={`form-text ${currentValue.length > fieldDef.maxLength * 0.9 ? 'text-warning' : 'text-muted'}`}>
                {currentValue.length} / {fieldDef.maxLength}
              </small>
            )}
          </div>
        );
      }

      // Handle textarea fields
      if (fieldDef.type === 'text') {
        const currentValue = item[fieldName] || '';
        return (
          <div key={fieldName} className="mb-3">
            <label className="form-label fw-semibold small">
              <i className="fa fa-align-left me-1 text-primary"></i>
              {fieldDef.label || fieldName}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </label>
            <textarea
              className="form-control"
              rows="3"
              value={currentValue}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = { ...newItems[index], [fieldName]: e.target.value };
                handleContentChange(fieldKey, newItems);
              }}
              placeholder={fieldDef.placeholder || `Enter ${fieldDef.label || fieldName}`}
              maxLength={fieldDef.maxLength}
            />
            {currentValue && fieldDef.maxLength && (
              <small className={`form-text ${currentValue.length > fieldDef.maxLength * 0.9 ? 'text-warning' : 'text-muted'}`}>
                {currentValue.length} / {fieldDef.maxLength}
              </small>
            )}
          </div>
        );
      }

      // Handle number fields
      if (fieldDef.type === 'number') {
        return (
          <div key={fieldName} className="mb-3">
            <label className="form-label fw-semibold small">
              <i className="fa fa-hashtag me-1 text-primary"></i>
              {fieldDef.label || fieldName}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </label>
            <input
              type="number"
              className="form-control"
              value={item[fieldName] || 0}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = { ...newItems[index], [fieldName]: parseInt(e.target.value) || 0 };
                handleContentChange(fieldKey, newItems);
              }}
              placeholder={fieldDef.placeholder || '0'}
            />
          </div>
        );
      }

      // Handle boolean fields
      if (fieldDef.type === 'boolean') {
        return (
          <div key={fieldName} className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id={`${fieldKey}-${index}-${fieldName}`}
              checked={item[fieldName] !== false}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = { ...newItems[index], [fieldName]: e.target.checked };
                handleContentChange(fieldKey, newItems);
              }}
            />
            <label className="form-check-label fw-semibold" htmlFor={`${fieldKey}-${index}-${fieldName}`}>
              <i className="fa fa-toggle-on me-1 text-primary"></i>
              {fieldDef.label || fieldName}
            </label>
          </div>
        );
      }

      return null;
    };

    // Create default item based on schema
    const createDefaultItem = () => {
      const newItem = {};
      Object.entries(fields).forEach(([fieldName, fieldDef]) => {
        if (fieldDef.type === 'image' || fieldDef.type === 'file' || fieldDef.type === 'object') {
          newItem[fieldName] = { location: '' };
        } else if (fieldDef.type === 'boolean') {
          newItem[fieldName] = fieldDef.defaultValue !== undefined ? fieldDef.defaultValue : (fieldDef.default !== undefined ? fieldDef.default : true);
        } else if (fieldDef.type === 'number') {
          newItem[fieldName] = fieldDef.defaultValue !== undefined ? fieldDef.defaultValue : (fieldDef.default !== undefined ? fieldDef.default : 0);
        } else {
          newItem[fieldName] = '';
        }
      });
      return newItem;
    };

    return (
      <div className="mb-4">
        <label className="form-label fw-semibold d-flex align-items-center gap-2">
          <i className="fa fa-th-large text-primary"></i>
          {fieldSchema.label || fieldKey}
          {fieldSchema.required && <span className="text-danger"> *</span>}
          <span className="badge bg-secondary ms-auto">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
        </label>

        {fieldSchema.description && (
          <div className="form-text mb-3">
            <i className="fa fa-info-circle text-info me-1"></i>
            {fieldSchema.description}
          </div>
        )}

        {items.length === 0 && (
          <div className="alert alert-light border mb-3" role="alert">
            <i className="fa fa-inbox text-muted me-2"></i>
            No items yet. Click &ldquo;Add Item&rdquo; below to get started.
          </div>
        )}

        <div className="row g-3">
          {items.map((item, index) => (
            <div key={index} className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <strong className="text-muted">
                    <i className="fa fa-grip-vertical me-2"></i>
                    Item {index + 1}
                  </strong>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete Item ${index + 1}?`)) {
                        const newItems = items.filter((_, i) => i !== index);
                        handleContentChange(fieldKey, newItems);
                      }
                    }}
                    title="Delete item"
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
                <div className="card-body">
                  {/* Render all fields for this item */}
                  {Object.entries(fields).map(([fieldName, fieldDef]) =>
                    renderItemField(item, index, fieldName, fieldDef)
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add New Button */}
          <div className="col-md-4">
            <div
              className="card h-100 d-flex align-items-center justify-content-center"
              style={{
                minHeight: '300px',
                border: '3px dashed #007bff',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onClick={() => {
                handleContentChange(fieldKey, [...items, createDefaultItem()]);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e7f3ff';
                e.currentTarget.style.borderColor = '#0056b3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.borderColor = '#007bff';
              }}
            >
              <div className="text-center">
                <i className="fa fa-plus-circle fa-4x text-primary mb-3"></i>
                <div className="text-primary fw-bold fs-5">Add New Item</div>
                <small className="text-muted">Click to create</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // === ARRAY OF STRINGS (like perfectFor) ===
  const renderArrayOfStrings = (fieldKey, fieldSchema) => {
    const items = content[fieldKey] || [];

    return (
      <div className="mb-4">
        <label className="form-label fw-semibold d-flex align-items-center gap-2">
          <i className="fa fa-list-ul text-primary"></i>
          {fieldSchema.label || fieldKey}
          {fieldSchema.required && <span className="text-danger"> *</span>}
          <span className="badge bg-secondary ms-auto">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
        </label>

        {fieldSchema.description && (
          <div className="form-text mb-3">
            <i className="fa fa-info-circle text-info me-1"></i>
            {fieldSchema.description}
          </div>
        )}

        {items.length === 0 && (
          <div className="alert alert-light border mb-3" role="alert">
            <i className="fa fa-inbox text-muted me-2"></i>
            No items yet. Click &ldquo;Add Item&rdquo; below to get started.
          </div>
        )}

        {items.map((item, index) => (
          <div key={index} className="input-group input-group-lg mb-3">
            <span className="input-group-text bg-light">
              <i className="fa fa-grip-vertical text-muted me-2"></i>
              #{index + 1}
            </span>
            <input
              type="text"
              className="form-control form-control-lg"
              value={item}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = e.target.value;
                handleContentChange(fieldKey, newItems);
              }}
              placeholder={fieldSchema.placeholder || `Enter item ${index + 1}`}
              maxLength={fieldSchema.maxLength}
            />
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => {
                const newItems = items.filter((_, i) => i !== index);
                handleContentChange(fieldKey, newItems);
              }}
              title="Remove item"
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            handleContentChange(fieldKey, [...items, '']);
          }}
        >
          <i className="fa fa-plus-circle me-2"></i>
          Add Item
        </button>
      </div>
    );
  };

  // === OBJECT (like pricing, sideColumnContent) ===
  const renderObject = (fieldKey, fieldSchema) => {
    const obj = content[fieldKey] || {};
    const fields = fieldSchema.fields || {};

    return (
      <div className="mb-4">
        <label className="form-label fw-semibold d-flex align-items-center gap-2">
          <i className="fa fa-folder-open text-primary"></i>
          {fieldSchema.label || fieldKey}
          {fieldSchema.required && <span className="text-danger"> *</span>}
        </label>

        {fieldSchema.description && (
          <div className="form-text mb-3">
            <i className="fa fa-info-circle text-info me-1"></i>
            {fieldSchema.description}
          </div>
        )}

        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <strong className="text-muted">
              <i className="fa fa-cog me-2"></i>
              {fieldSchema.label || fieldKey} Settings
            </strong>
          </div>
          <div className="card-body">
            {Object.entries(fields).map(([nestedKey, nestedField]) => {
              // Handle file type in nested objects
              if (nestedField.type === 'file' || nestedField.type === 'image') {
                const uploadKey = `nested-${fieldKey}-${nestedKey}`;
                const isUploading = uploadingImages[uploadKey];
                const imageUrl = obj[nestedKey]?.location || '';

                return (
                  <div key={nestedKey} className="mb-4">
                    <label className="form-label fw-semibold d-flex align-items-center gap-2">
                      <i className="fa fa-image text-primary"></i>
                      {nestedField.label || nestedKey}
                      {nestedField.required && <span className="text-danger"> *</span>}
                    </label>

                    {imageUrl ? (
                      <div className="mb-2 position-relative">
                        <img
                          src={imageUrl}
                          alt={nestedField.label || nestedKey}
                          className="img-fluid rounded"
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            border: '2px solid #e0e0e0',
                            backgroundColor: '#f8f9fa',
                            padding: '0.5rem',
                          }}
                        />
                        <div className="position-absolute top-0 end-0 m-2">
                          <span className="badge bg-success">
                            <i className="fa fa-check"></i>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="mb-2 d-flex flex-column align-items-center justify-content-center bg-light rounded"
                        style={{
                          width: '100%',
                          height: '200px',
                          border: '2px dashed #dee2e6',
                        }}
                      >
                        <i className="fa fa-cloud-upload-alt fa-3x text-muted mb-2"></i>
                        <small className="text-muted">No image uploaded</small>
                      </div>
                    )}

                    <label className={`btn btn-sm ${imageUrl ? 'btn-outline-primary' : 'btn-primary'} w-100`}>
                      <i className={`fa ${isUploading ? 'fa-spinner fa-spin' : 'fa-upload'} me-1`}></i>
                      {isUploading ? 'Uploading...' : imageUrl ? 'Replace Image' : 'Upload Image'}
                      <input
                        type="file"
                        accept={nestedField.accept || 'image/*'}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));
                            handleImageUpload(file, (url) => {
                              handleContentChange(fieldKey, {
                                ...obj,
                                [nestedKey]: { location: url },
                              });
                              setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
                            });
                          }
                          e.target.value = null;
                        }}
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                );
              }

              // Handle select dropdown with options
              if (nestedField.options && Array.isArray(nestedField.options)) {
                return (
                  <div key={nestedKey} className="mb-4">
                    <label className="form-label fw-semibold">
                      <i className="fa fa-list-ul me-2 text-primary"></i>
                      {nestedField.label || nestedKey}
                      {nestedField.required && <span className="text-danger"> *</span>}
                    </label>
                    <select
                      className="form-select form-select-lg"
                      value={obj[nestedKey] || nestedField.defaultValue || ''}
                      onChange={(e) => {
                        handleContentChange(fieldKey, {
                          ...obj,
                          [nestedKey]: e.target.value,
                        });
                      }}
                    >
                      {nestedField.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {nestedField.description && (
                      <div className="form-text">
                        <i className="fa fa-info-circle text-info me-1"></i>
                        {nestedField.description}
                      </div>
                    )}
                  </div>
                );
              }

              // Handle textarea
              if (nestedField.type === 'text') {
                const currentValue = obj[nestedKey] || '';
                const charWarning = nestedField.maxLength && currentValue.length > nestedField.maxLength * 0.9;

                return (
                  <div key={nestedKey} className="mb-4">
                    <label className="form-label fw-semibold">
                      <i className="fa fa-align-left me-2 text-primary"></i>
                      {nestedField.label || nestedKey}
                      {nestedField.required && <span className="text-danger"> *</span>}
                      {nestedField.maxLength && (
                        <span className="badge bg-secondary ms-2">Max: {nestedField.maxLength} chars</span>
                      )}
                    </label>
                    <textarea
                      className="form-control form-control-lg"
                      rows="4"
                      value={currentValue}
                      onChange={(e) => {
                        handleContentChange(fieldKey, {
                          ...obj,
                          [nestedKey]: e.target.value,
                        });
                      }}
                      placeholder={nestedField.placeholder || `Enter ${nestedField.label || nestedKey}`}
                      maxLength={nestedField.maxLength}
                    />
                    {currentValue && nestedField.maxLength && (
                      <div className={`form-text ${charWarning ? 'text-warning fw-bold' : 'text-muted'}`}>
                        {currentValue.length} / {nestedField.maxLength} characters
                      </div>
                    )}
                    {nestedField.description && (
                      <div className="form-text">
                        <i className="fa fa-info-circle text-info me-1"></i>
                        {nestedField.description}
                      </div>
                    )}
                  </div>
                );
              }

              // Default: text input
              const currentValue = obj[nestedKey] || '';
              const charWarning = nestedField.maxLength && currentValue.length > nestedField.maxLength * 0.9;

              return (
                <div key={nestedKey} className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fa fa-font me-2 text-primary"></i>
                    {nestedField.label || nestedKey}
                    {nestedField.required && <span className="text-danger"> *</span>}
                    {nestedField.maxLength && (
                      <span className="badge bg-secondary ms-2">Max: {nestedField.maxLength} chars</span>
                    )}
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    value={currentValue}
                    onChange={(e) => {
                      handleContentChange(fieldKey, {
                        ...obj,
                        [nestedKey]: e.target.value,
                      });
                    }}
                    placeholder={nestedField.placeholder || `Enter ${nestedField.label || nestedKey}`}
                    maxLength={nestedField.maxLength}
                  />
                  {currentValue && nestedField.maxLength && (
                    <div className={`form-text ${charWarning ? 'text-warning fw-bold' : 'text-muted'}`}>
                      {currentValue.length} / {nestedField.maxLength} characters
                    </div>
                  )}
                  {nestedField.description && (
                    <div className="form-text">
                      <i className="fa fa-info-circle text-info me-1"></i>
                      {nestedField.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render simple text field
  const renderTextField = (fieldKey, fieldSchema, currentValue) => {
    const isTextarea = fieldSchema.type === 'text' || fieldSchema.type === 'textarea' || (fieldSchema.maxLength && fieldSchema.maxLength > 200);

    // Check if field has options (dropdown select)
    if (fieldSchema.options && Array.isArray(fieldSchema.options)) {
      return (
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className="fa fa-list-ul me-2 text-primary"></i>
            {fieldSchema.label || fieldKey}
            {fieldSchema.required && <span className="text-danger"> *</span>}
          </label>
          <select
            className="form-select form-select-lg"
            value={currentValue || fieldSchema.defaultValue || ''}
            onChange={(e) => handleContentChange(fieldKey, e.target.value)}
          >
            {fieldSchema.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {fieldSchema.description && (
            <div className="form-text text-muted">
              <i className="fa fa-info-circle me-1"></i>
              {fieldSchema.description}
            </div>
          )}
        </div>
      );
    }

    // Special handling for code textarea fields (custom_section)
    const isCodeField = fieldSchema.type === 'textarea' && (fieldKey === 'html' || fieldKey === 'css' || fieldKey === 'javascript');

    return (
      <div className="mb-4">
        <label className="form-label fw-semibold">
          <i className={`fa ${isTextarea ? 'fa-code' : 'fa-font'} me-2 text-primary`}></i>
          {fieldSchema.label || fieldKey}
          {fieldSchema.required && <span className="text-danger"> *</span>}
          {fieldSchema.maxLength && (
            <span className="badge bg-secondary ms-2 fw-normal">
              Max: {fieldSchema.maxLength} chars
            </span>
          )}
        </label>
        {isTextarea ? (
          <textarea
            className={`form-control ${isCodeField ? 'font-monospace' : 'form-control-lg'}`}
            rows={fieldSchema.rows || 5}
            value={currentValue || ''}
            onChange={(e) => handleContentChange(fieldKey, e.target.value)}
            placeholder={fieldSchema.placeholder || `Enter ${fieldSchema.label || fieldKey}...`}
            maxLength={fieldSchema.maxLength}
            style={isCodeField ? { fontSize: '0.875rem', lineHeight: '1.5' } : {}}
          />
        ) : (
          <input
            type="text"
            className="form-control form-control-lg"
            value={currentValue || ''}
            onChange={(e) => handleContentChange(fieldKey, e.target.value)}
            placeholder={fieldSchema.placeholder || `Enter ${fieldSchema.label || fieldKey}...`}
            maxLength={fieldSchema.maxLength}
          />
        )}
        {currentValue && fieldSchema.maxLength && (
          <div className="form-text">
            <span className={currentValue.length > fieldSchema.maxLength * 0.9 ? 'text-warning' : 'text-muted'}>
              {currentValue.length} / {fieldSchema.maxLength} characters
            </span>
          </div>
        )}
        {fieldSchema.helpText && (
          <div className="form-text text-muted">
            <i className="fa fa-info-circle me-1"></i>
            {fieldSchema.helpText}
          </div>
        )}
        {fieldSchema.description && !fieldSchema.helpText && (
          <div className="form-text text-muted">
            <i className="fa fa-info-circle me-1"></i>
            {fieldSchema.description}
          </div>
        )}
      </div>
    );
  };

  // === FILE UPLOAD RENDERER (root level) ===
  const renderFileField = (fieldKey, fieldSchema, currentValue) => {
    const uploadKey = `root-${fieldKey}`;
    const isUploading = uploadingImages[uploadKey];
    const imageUrl = currentValue?.location || '';

    return (
      <div className="mb-4">
        <label className="form-label fw-semibold d-flex align-items-center gap-2">
          <i className="fa fa-image text-primary"></i>
          {fieldSchema.label || fieldKey}
          {fieldSchema.required && <span className="text-danger"> *</span>}
        </label>

        {fieldSchema.description && (
          <div className="form-text mb-3">
            <i className="fa fa-info-circle text-info me-1"></i>
            {fieldSchema.description}
          </div>
        )}

        {/* Image Preview */}
        {imageUrl ? (
          <div className="mb-3 position-relative">
            <img
              src={imageUrl}
              alt={fieldSchema.label || fieldKey}
              className="img-fluid rounded shadow-sm"
              style={{
                width: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                border: '2px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                padding: '1rem',
              }}
            />
            <div className="position-absolute top-0 end-0 m-2">
              <span className="badge bg-success">
                <i className="fa fa-check me-1"></i>
                Uploaded
              </span>
            </div>
          </div>
        ) : (
          <div
            className="mb-3 d-flex flex-column align-items-center justify-content-center bg-light rounded"
            style={{
              width: '100%',
              height: '300px',
              border: '3px dashed #dee2e6',
            }}
          >
            <i className="fa fa-cloud-upload-alt fa-4x text-muted mb-3"></i>
            <p className="text-muted mb-0">No image uploaded yet</p>
            <small className="text-muted">Click below to upload</small>
          </div>
        )}

        {/* Upload Button */}
        <label className={`btn ${imageUrl ? 'btn-outline-primary' : 'btn-primary'} w-100`} style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
          <i className={`fa ${isUploading ? 'fa-spinner fa-spin' : 'fa-upload'} me-2`}></i>
          {isUploading ? 'Uploading...' : imageUrl ? 'Replace Image' : 'Upload Image'}
          <input
            type="file"
            accept={fieldSchema.accept || 'image/*'}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));
                handleImageUpload(file, (url) => {
                  handleContentChange(fieldKey, { location: url });
                  setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
                });
              }
              e.target.value = null;
            }}
            disabled={isUploading}
          />
        </label>

        {!isUploading && (
          <div className="form-text mt-2">
            <i className="fa fa-lightbulb text-warning me-1"></i>
            Supported formats: JPG, PNG, GIF, WebP (Max 5MB)
          </div>
        )}
      </div>
    );
  };

  // === MAIN FIELD RENDERER ===
  const renderField = (fieldKey, fieldSchema) => {
    const currentValue = content[fieldKey];

    // Array of objects (heroImages, items with fields definition)
    if (fieldSchema.type === 'array' && (fieldSchema.itemType === 'object' || fieldSchema.fields)) {
      return renderArrayOfObjects(fieldKey, fieldSchema);
    }

    // Array of strings (perfectFor)
    if (fieldSchema.type === 'array' && fieldSchema.itemType === 'string') {
      return renderArrayOfStrings(fieldKey, fieldSchema);
    }

    // Object (pricing)
    if (fieldSchema.type === 'object') {
      return renderObject(fieldKey, fieldSchema);
    }

    // File upload (root level)
    if (fieldSchema.type === 'file' || fieldSchema.type === 'image') {
      return renderFileField(fieldKey, fieldSchema, currentValue);
    }

    // String/text/textarea fields
    if (fieldSchema.type === 'string' || fieldSchema.type === 'text' || fieldSchema.type === 'textarea') {
      return renderTextField(fieldKey, fieldSchema, currentValue);
    }

    // Boolean fields (root level)
    if (fieldSchema.type === 'boolean') {
      return (
        <div key={fieldKey} className="mb-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id={`field-${fieldKey}`}
              checked={currentValue !== false}
              onChange={(e) => handleContentChange(fieldKey, e.target.checked)}
            />
            <label className="form-check-label fw-semibold" htmlFor={`field-${fieldKey}`}>
              <i className="fa fa-toggle-on me-2 text-primary"></i>
              {fieldSchema.label || fieldKey}
              {fieldSchema.required && <span className="text-danger"> *</span>}
            </label>
          </div>
          {fieldSchema.helpText && (
            <div className="form-text text-muted ms-4">
              <i className="fa fa-info-circle me-1"></i>
              {fieldSchema.helpText}
            </div>
          )}
          {fieldSchema.description && !fieldSchema.helpText && (
            <div className="form-text text-muted ms-4">
              <i className="fa fa-info-circle me-1"></i>
              {fieldSchema.description}
            </div>
          )}
        </div>
      );
    }

    // Fallback
    return (
      <div className="mb-3">
        <label className="form-label">{fieldSchema.label || fieldKey}</label>
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          Field type &quot;{fieldSchema.type}&quot; editor coming soon
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading schema...</p>
      </div>
    );
  }

  return (
    <div className="section-editor-container">
      {/* Section Name Input */}
      <div className="mb-3">
        <label className="form-label fw-bold">
          <i className="fa fa-tag me-2"></i>
          Section Name
        </label>
        <input
          type="text"
          className="form-control"
          value={sectionName}
          onChange={(e) => {
            setSectionName(e.target.value);
            setHasChanges(true);
          }}
          placeholder="e.g., News & Brands, Payment Options, Hero Section..."
        />
        <div className="form-text">
          Give this section a descriptive name to easily identify it in the CMS
        </div>
      </div>

      <div className="mb-3">
        <div className="alert alert-info">
          <strong>Component:</strong> {section.componentKey}
          <br />
          <strong>Section ID:</strong> {section.sectionId}
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <i className="fa fa-edit me-2"></i>
            Content
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'styles' ? 'active' : ''}`}
            onClick={() => setActiveTab('styles')}
          >
            <i className="fa fa-palette me-2"></i>
            Styles
          </button>
        </li>
      </ul>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="mb-4">
          {componentSchema && Object.keys(componentSchema).length > 0 ? (
            <>
              {/* Info Banner */}
              <div className="alert alert-info d-flex align-items-center mb-4">
                <i className="fa fa-info-circle me-2"></i>
                <div className="flex-grow-1">
                  <strong>Content Editor</strong>
                  <p className="mb-0 small">Configure the content that will be displayed in this section. Required fields are marked with <span className="text-danger">*</span></p>
                </div>
              </div>

              {/* Content Fields */}
              <div className="content-fields">
                {Object.entries(componentSchema)
                  .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
                  .map(([fieldKey, fieldSchema]) => (
                    <div key={fieldKey} className="content-field-wrapper">
                      {renderField(fieldKey, fieldSchema)}
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle me-2"></i>
              No schema defined for this component. Using fallback editor.
            </div>
          )}
        </div>
      )}

      {/* Styles Tab */}
      {activeTab === 'styles' && (
        <div className="mb-4">
          {styleSchema && Object.keys(styleSchema).length > 0 ? (
            <>
              {/* Search Bar */}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fa fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search style fields..."
                    value={styleSearchQuery}
                    onChange={(e) => setStyleSearchQuery(e.target.value)}
                  />
                  {styleSearchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setStyleSearchQuery('')}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Grouped Accordion */}
              <div className="accordion" id="styleAccordion">
                {(() => {
                  const grouped = groupStyleFields(styleSchema);
                  const searchLower = styleSearchQuery.toLowerCase();

                  return Object.entries(grouped).map(([groupKey, groupData]) => {
                    // Filter fields based on search
                    const filteredFields = styleSearchQuery
                      ? groupData.fields.filter(([key]) => key.toLowerCase().includes(searchLower))
                      : groupData.fields;

                    if (filteredFields.length === 0) return null;

                    const isExpanded = expandedGroups[groupKey] !== false;

                    return (
                      <div className="accordion-item" key={groupKey}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${!isExpanded ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <i className={`fa ${groupData.icon} me-2`}></i>
                            {groupData.label}
                            <span className="badge bg-secondary ms-2">{filteredFields.length}</span>
                          </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                          <div className="accordion-body">
                            {filteredFields.map(([styleKey, schemaOrValue]) => renderStyleField(styleKey, schemaOrValue))}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          ) : (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle me-2"></i>
              No styles defined for this component.
            </div>
          )}
        </div>
      )}

      {/* OLD HARDCODED STYLES - TO BE REMOVED */}
      {activeTab === 'styles-old' && (
        <div className="mb-4">
          {styleSchema && Object.keys(styleSchema).length > 0 ? (
            <div className="row">
              {/* Background & Section Styles */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-paint-brush me-2"></i>
                  Background & Section
                </h6>

                {/* Background Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Background Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.backgroundColor || '#f6f5f3'}
                        onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.backgroundColor || '#f6f5f3'}
                        onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                        placeholder="#f6f5f3"
                      />
                    </div>
                  </div>
                </div>

                {/* Use Gradient */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Use Gradient</label>
                  <div className="col-sm-8">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={styles.useGradient || false}
                        onChange={(e) => handleStyleChange('useGradient', e.target.checked)}
                      />
                    </div>
                  </div>
                </div>

                {styles.useGradient && (
                  <>
                    {/* Gradient Type */}
                    <div className="row mb-3">
                      <label className="col-sm-4 col-form-label">Gradient Type</label>
                      <div className="col-sm-8">
                        <select
                          className="form-select"
                          value={styles.gradientType || 'linear'}
                          onChange={(e) => handleStyleChange('gradientType', e.target.value)}
                        >
                          <option value="linear">Linear</option>
                          <option value="radial">Radial</option>
                        </select>
                      </div>
                    </div>

                    {/* Gradient Angle */}
                    <div className="row mb-3">
                      <label className="col-sm-4 col-form-label">Gradient Angle</label>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          className="form-control"
                          value={styles.gradientAngle || '180deg'}
                          onChange={(e) => handleStyleChange('gradientAngle', e.target.value)}
                          placeholder="180deg"
                        />
                      </div>
                    </div>

                    {/* Gradient Start */}
                    <div className="row mb-3">
                      <label className="col-sm-4 col-form-label">Gradient Start</label>
                      <div className="col-sm-8">
                        <div className="input-group">
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={styles.gradientStart || '#ffffff'}
                            onChange={(e) => handleStyleChange('gradientStart', e.target.value)}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={styles.gradientStart || '#ffffff'}
                            onChange={(e) => handleStyleChange('gradientStart', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Gradient End */}
                    <div className="row mb-3">
                      <label className="col-sm-4 col-form-label">Gradient End</label>
                      <div className="col-sm-8">
                        <div className="input-group">
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={styles.gradientEnd || '#f0f0f0'}
                            onChange={(e) => handleStyleChange('gradientEnd', e.target.value)}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={styles.gradientEnd || '#f0f0f0'}
                            onChange={(e) => handleStyleChange('gradientEnd', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Section Padding */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Section Padding</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.sectionPadding || 'py-1 py-md-5'}
                      onChange={(e) => handleStyleChange('sectionPadding', e.target.value)}
                      placeholder="py-1 py-md-5"
                    />
                    <small className="form-text text-muted">Bootstrap spacing classes</small>
                  </div>
                </div>

                {/* Section Min Height */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Min Height</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.sectionMinHeight || '50vh'}
                      onChange={(e) => handleStyleChange('sectionMinHeight', e.target.value)}
                      placeholder="50vh"
                    />
                  </div>
                </div>
              </div>

              {/* Image Carousel Styles */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-image me-2"></i>
                  Image Carousel
                </h6>

                {/* Image Border Radius */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Border Radius</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.imageBorderRadius || '24px'}
                      onChange={(e) => handleStyleChange('imageBorderRadius', e.target.value)}
                      placeholder="24px"
                    />
                  </div>
                </div>

                {/* Thumbnail Border Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Thumbnail Border</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.thumbnailBorderColor || '#c47662'}
                        onChange={(e) => handleStyleChange('thumbnailBorderColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.thumbnailBorderColor || '#c47662'}
                        onChange={(e) => handleStyleChange('thumbnailBorderColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Carousel Dot Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Dot Color</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.carouselDotColor || 'rgba(255, 255, 255, 0.4)'}
                      onChange={(e) => handleStyleChange('carouselDotColor', e.target.value)}
                      placeholder="rgba(255, 255, 255, 0.4)"
                    />
                  </div>
                </div>

                {/* Carousel Dot Active Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Active Dot Color</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.carouselDotActiveColor || '#ffffff'}
                      onChange={(e) => handleStyleChange('carouselDotActiveColor', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
              </div>

              {/* Typography Styles */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-font me-2"></i>
                  Typography
                </h6>

                {/* Heading Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Heading Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.headingColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('headingColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.headingColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('headingColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Heading Font Sizes */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Heading Size (Mobile)</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.headingFontSize || '2rem'}
                      onChange={(e) => handleStyleChange('headingFontSize', e.target.value)}
                      placeholder="2rem"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Heading Size (Tablet)</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.headingFontSizeMd || '2.5rem'}
                      onChange={(e) => handleStyleChange('headingFontSizeMd', e.target.value)}
                      placeholder="2.5rem"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Heading Size (Desktop)</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.headingFontSizeLg || '3rem'}
                      onChange={(e) => handleStyleChange('headingFontSizeLg', e.target.value)}
                      placeholder="3rem"
                    />
                  </div>
                </div>

                {/* Text Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Text Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.textColor || '#666666'}
                        onChange={(e) => handleStyleChange('textColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.textColor || '#666666'}
                        onChange={(e) => handleStyleChange('textColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Subtitle Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Subtitle Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.subtitleColor || '#666666'}
                        onChange={(e) => handleStyleChange('subtitleColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.subtitleColor || '#666666'}
                        onChange={(e) => handleStyleChange('subtitleColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Perfect For Title Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Perfect For Title</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.perfectForTitleColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('perfectForTitleColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.perfectForTitleColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('perfectForTitleColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Card Styles */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-credit-card me-2"></i>
                  Pricing Card
                </h6>

                {/* Pricing Card Background */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Background</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.pricingCardBackground || '#ffffff'}
                        onChange={(e) => handleStyleChange('pricingCardBackground', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.pricingCardBackground || '#ffffff'}
                        onChange={(e) => handleStyleChange('pricingCardBackground', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing Card Border */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Border Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.pricingCardBorder || '#dee2e6'}
                        onChange={(e) => handleStyleChange('pricingCardBorder', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.pricingCardBorder || '#dee2e6'}
                        onChange={(e) => handleStyleChange('pricingCardBorder', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing Card Border Radius */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Border Radius</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.pricingCardBorderRadius || '1rem'}
                      onChange={(e) => handleStyleChange('pricingCardBorderRadius', e.target.value)}
                      placeholder="1rem"
                    />
                  </div>
                </div>

                {/* Discount Label Background */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Discount Label Bg</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.discountLabelBackground || '#198754'}
                        onChange={(e) => handleStyleChange('discountLabelBackground', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.discountLabelBackground || '#198754'}
                        onChange={(e) => handleStyleChange('discountLabelBackground', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Discount Label Text Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Discount Label Text</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.discountLabelTextColor || '#ffffff'}
                        onChange={(e) => handleStyleChange('discountLabelTextColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.discountLabelTextColor || '#ffffff'}
                        onChange={(e) => handleStyleChange('discountLabelTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Original Price Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Original Price</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.originalPriceColor || '#945c4d'}
                        onChange={(e) => handleStyleChange('originalPriceColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.originalPriceColor || '#945c4d'}
                        onChange={(e) => handleStyleChange('originalPriceColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Discounted Price Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Discounted Price</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.discountedPriceColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('discountedPriceColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.discountedPriceColor || '#2c2c2c'}
                        onChange={(e) => handleStyleChange('discountedPriceColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button Styles */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-hand-pointer me-2"></i>
                  CTA Button
                </h6>

                {/* Button Background */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Background</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.buttonBackground || '#945c4d'}
                        onChange={(e) => handleStyleChange('buttonBackground', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.buttonBackground || '#945c4d'}
                        onChange={(e) => handleStyleChange('buttonBackground', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Button Text Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Text Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.buttonTextColor || '#ffffff'}
                        onChange={(e) => handleStyleChange('buttonTextColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.buttonTextColor || '#ffffff'}
                        onChange={(e) => handleStyleChange('buttonTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Button Hover Background */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Hover Background</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.buttonHoverBackground || '#7d4d3f'}
                        onChange={(e) => handleStyleChange('buttonHoverBackground', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.buttonHoverBackground || '#7d4d3f'}
                        onChange={(e) => handleStyleChange('buttonHoverBackground', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Button Border Radius */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Border Radius</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.buttonBorderRadius || '0.5rem'}
                      onChange={(e) => handleStyleChange('buttonBorderRadius', e.target.value)}
                      placeholder="0.5rem"
                    />
                  </div>
                </div>

                {/* Button Shadow Color */}
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Shadow Color</label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      value={styles.buttonShadowColor || 'rgba(148, 92, 77, 0.3)'}
                      onChange={(e) => handleStyleChange('buttonShadowColor', e.target.value)}
                      placeholder="rgba(148, 92, 77, 0.3)"
                    />
                  </div>
                </div>
              </div>

              {/* Accent Color */}
              <div className="col-12 mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fa fa-star me-2"></i>
                  Accent
                </h6>

                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label">Accent Color</label>
                  <div className="col-sm-8">
                    <div className="input-group">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={styles.accentColor || '#945c4d'}
                        onChange={(e) => handleStyleChange('accentColor', e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={styles.accentColor || '#945c4d'}
                        onChange={(e) => handleStyleChange('accentColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle me-2"></i>
              No styles defined for this component.
            </div>
          )}
        </div>
      )}

      {/* Preview Modal for Custom Section */}
      {showPreview && previewContent && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowPreview(false)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-eye me-2"></i>
                  Custom Section Preview
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreview(false)}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <iframe
                  style={{ width: '100%', minHeight: '500px', border: 'none' }}
                  title="Custom Section Preview"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                          * { box-sizing: border-box; }
                          body {
                            margin: 0;
                            padding: 20px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                          }
                          ${previewContent.css || ''}
                        </style>
                      </head>
                      <body>
                        ${previewContent.html || '<p class="text-muted">No HTML content</p>'}
                        <script>
                          try {
                            ${previewContent.javascript || ''}
                          } catch (error) {
                            console.error('Preview JavaScript error:', error);
                          }
                        </script>
                      </body>
                    </html>
                  `}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPreview(false)}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="d-flex gap-2">
        {section.componentKey === 'custom_section' && (
          <button
            type="button"
            className="btn btn-info"
            onClick={handlePreview}
          >
            <i className="fa fa-eye me-2"></i>
            Preview
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary flex-grow-1"
          onClick={handleSave}
          disabled={!hasChanges}
        >
          <i className="fa fa-save me-2"></i>
          {hasChanges ? 'Save Changes' : 'No Changes'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
        >
          <i className="fa fa-times me-2"></i>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={onDelete}
        >
          <i className="fa fa-trash me-2"></i>
          Delete Section
        </button>
      </div>

      {hasChanges && (
        <div className="alert alert-warning mt-3 mb-0">
          <i className="fa fa-exclamation-triangle me-2"></i>
          You have unsaved changes
        </div>
      )}
    </div>
  );
};

export default SectionEditor;
